import React, { Component } from "react";

import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import Container from "@cloudscape-design/components/container";
import FormField from "@cloudscape-design/components/form-field";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Modal from "@cloudscape-design/components/modal";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";

const EMPTY_OPTIONS = { geo: [], paymentType: [], trafficType: [] };
const EMPTY_VALUES = { geo: "", paymentType: "", trafficType: "" };
const EMPTY_OPTION_DRAFTS = { paymentType: "", trafficType: "" };

class PlayerProfilePartnerMetadataSection extends Component {
	constructor(props) {
		super(props);
		this.state = {
			loaded: false,
			error: null,
			partnerName: "",
			...EMPTY_VALUES,
			options: EMPTY_OPTIONS,
			optionDrafts: { ...EMPTY_OPTION_DRAFTS },
			removeSelections: { paymentType: "", trafficType: "" },
			optionsModalVisible: false,
			optionsSaving: null,
			optionError: null,
			saving: false,
			saveError: null,
		};
		this.unmounted = false;
		this.loadRequestId = 0;
	}

	componentDidMount() {
		this.loadPartnerMetadata();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.uid !== this.props.uid || prevProps.project !== this.props.project || prevProps.user !== this.props.user) {
			this.loadPartnerMetadata();
		}
	}

	componentWillUnmount() {
		this.unmounted = true;
		this.loadRequestId += 1;
	}

	isCurrentRequest(requestId, uid, project) {
		return !this.unmounted && requestId === this.loadRequestId && uid === this.props.uid &&
			String(project || 0) === String(this.props.project || 0);
	}

	getOption(field, value) {
		if (!value) return null;
		return this.state.options[field].find((option) => option.value === value) || {
			value,
			label: field === "paymentType" && value === "Hybrid" ? "Гибрид" : value,
		};
	}

	loadPartnerMetadata() {
		const requestId = ++this.loadRequestId;
		const uid = this.props.uid;
		const project = this.props.project;
		this.setState({ loaded: false, error: null, saveError: null, optionError: null, optionsModalVisible: false });

		Promise.all([
			this.props.Interface.request("get", { class: "users", module: "data", method: "getPartnerMetadata" }, { uid, project })
				.catch((error) => Promise.reject({ source: "Partner metadata", error })),
			this.props.Interface.request("get", { class: "users", module: "data", method: "getPartnerMetadataOptions" }, {})
				.catch((error) => Promise.reject({ source: "Partner options", error })),
		])
			.then(([metadata, options]) => {
				if (!this.isCurrentRequest(requestId, uid, project)) return;
				this.setState({
					loaded: true,
					error: null,
					partnerName: metadata?.partnerName || "",
					geo: metadata?.geo || "",
					paymentType: metadata?.paymentType || "",
					trafficType: metadata?.trafficType || "",
					options: options || EMPTY_OPTIONS,
					optionDrafts: { ...EMPTY_OPTION_DRAFTS },
					removeSelections: { paymentType: "", trafficType: "" },
					saving: false,
					saveError: null,
				});
			})
			.catch((failure) => {
				if (!this.isCurrentRequest(requestId, uid, project)) return;
				const error = failure.error || failure;
				const message = typeof error === "string" ? error : error?.text || error?.message || "Unknown error";
				this.setState({ loaded: true, error: `${failure.source || "Partner metadata"}: ${message}`, saving: false });
			});
	}

	async savePartnerMetadata() {
		const requestId = this.loadRequestId;
		const uid = this.props.uid;
		const project = this.props.project;
		this.setState({ saving: true, saveError: null });
		try {
			const response = await this.props.Interface.request(
				"set", { class: "users", module: "data", method: "partnerMetadata" },
				{ uid, project, partnerName: this.state.partnerName, geo: this.state.geo,
					paymentType: this.state.paymentType, trafficType: this.state.trafficType },
			);
			if (!this.isCurrentRequest(requestId, uid, project)) return;
			this.setState({
				saving: false,
				saveError: null,
				partnerName: response?.partnerName || "",
				geo: response?.geo || "",
				paymentType: response?.paymentType || "",
				trafficType: response?.trafficType || "",
			});
			this.props.Interface.addNoty("Success", "Partner metadata was successfully saved");
		} catch (error) {
			if (!this.isCurrentRequest(requestId, uid, project)) return;
			this.setState({ saving: false, saveError: error?.text || error?.message ||
				(typeof error === "string" ? error : "Couldn't save partner metadata") });
		}
	}

	async changeOption(field, action) {
		const value = action === "add"
			? this.state.optionDrafts[field].trim().replace(/\s+/g, " ")
			: this.state.removeSelections[field];
		if (!value || this.state.optionsSaving) return;
		const requestId = this.loadRequestId;
		const uid = this.props.uid;
		const project = this.props.project;
		this.setState({ optionsSaving: field, optionError: null });
		try {
			const options = await this.props.Interface.request("set", {
				class: "users", module: "data",
				method: action === "add" ? "addPartnerMetadataOption" : "removePartnerMetadataOption",
			}, { field, value });
			if (!this.isCurrentRequest(requestId, uid, project)) return;
			this.setState((state) => ({
				options,
				optionsSaving: null,
				optionDrafts: { ...state.optionDrafts, [field]: action === "add" ? "" : state.optionDrafts[field] },
				removeSelections: { ...state.removeSelections, [field]: "" },
			}));
		} catch (error) {
			if (!this.isCurrentRequest(requestId, uid, project)) return;
			this.setState({ optionsSaving: null, optionError: error?.text || error?.message ||
				(typeof error === "string" ? error : "Couldn't update options") });
		}
	}

	renderSelector(field, label) {
		return (
			<FormField label={label} stretch>
				<SpaceBetween size="xs" direction="vertical">
					<Select filteringType="auto" selectedOption={this.getOption(field, this.state[field])}
						options={this.state.options[field]}
						onChange={({ detail }) => this.setState({ [field]: detail.selectedOption?.value || "" })}
						placeholder={`Select ${label.toLowerCase()}`} disabled={this.state.saving} />
					<Button onClick={() => this.setState({ [field]: "" })}
						disabled={this.state.saving || !this.state[field]}>Clear</Button>
				</SpaceBetween>
			</FormField>
		);
	}

	renderOptionsEditor(field, label) {
		const busy = Boolean(this.state.optionsSaving || this.state.saving);
		const removeValue = this.state.removeSelections[field] || "";
		return (
			<Container header={<Header variant="h3">{label}</Header>}>
				<SpaceBetween size="m" direction="vertical">
					<FormField label="Add option">
						<SpaceBetween size="xs" direction="horizontal">
							<Input value={this.state.optionDrafts[field]}
								onChange={({ detail }) => this.setState((state) => ({ optionDrafts: { ...state.optionDrafts, [field]: detail.value } }))}
								placeholder={`New ${label.toLowerCase()}`} disabled={busy} />
							<Button onClick={() => this.changeOption(field, "add")}
								disabled={busy || !this.state.optionDrafts[field].trim()} loading={this.state.optionsSaving === field}>Add</Button>
						</SpaceBetween>
					</FormField>
					<FormField label="Remove option">
						<SpaceBetween size="xs" direction="horizontal">
							<Select selectedOption={this.state.options[field].find((option) => option.value === removeValue) || null}
								options={this.state.options[field]}
								onChange={({ detail }) => this.setState((state) => ({ removeSelections: { ...state.removeSelections, [field]: detail.selectedOption?.value || "" } }))}
								placeholder="Choose option to remove" disabled={busy} />
							<Button onClick={() => this.changeOption(field, "remove")}
								disabled={busy || !removeValue}>Remove</Button>
						</SpaceBetween>
					</FormField>
				</SpaceBetween>
			</Container>
		);
	}

	render() {
		const { loaded, error, partnerName, saving, saveError, optionError } = this.state;
		const canManageOptions = Number(this.props.user?.level) >= 6 ||
			this.props.user?.showColumn?.includes("managePartnerMetadataOptions");
		return (
			<Container width="full" header={<Header variant="h3" description="Partner account metadata for analytics and cohorts"
				actions={canManageOptions ? <Button iconName="settings" disabled={!loaded || Boolean(error) || saving}
					onClick={() => this.setState({ optionsModalVisible: true, optionError: null })}>Manage options</Button> : null}>Partner Metadata</Header>}>
				{!loaded ? <Spinner size="normal" /> : error ? (
					<Alert type="error">{error}</Alert>
				) : (
					<SpaceBetween size="m" direction="vertical">
						<FormField label="Partner Name" stretch>
							<Input value={partnerName} onChange={({ detail }) => this.setState({ partnerName: detail.value })} disabled={saving} />
						</FormField>
						{this.renderSelector("geo", "GEO")}
						{this.renderSelector("paymentType", "Payment Type")}
						{this.renderSelector("trafficType", "Traffic Type")}
						{saveError ? <Alert type="error">{saveError}</Alert> : null}
						<Box><Button variant="primary" loading={saving} disabled={Boolean(this.state.optionsSaving)}
							onClick={() => this.savePartnerMetadata()}>Save metadata</Button></Box>
					</SpaceBetween>
				)}
				{canManageOptions ? <Modal visible={this.state.optionsModalVisible} size="large"
					onDismiss={() => { if (!this.state.optionsSaving) this.setState({ optionsModalVisible: false, optionError: null }); }}
					header="Manage Payment and Traffic Types"
					footer={<Box float="right"><Button disabled={Boolean(this.state.optionsSaving)}
						onClick={() => this.setState({ optionsModalVisible: false, optionError: null })}>Close</Button></Box>}>
					<SpaceBetween size="m" direction="vertical">
						<Box variant="p">Options are shared across partners. Removing an option keeps values already saved for partners.</Box>
						{optionError ? <Alert type="error">{optionError}</Alert> : null}
						{this.renderOptionsEditor("paymentType", "Payment Type")}
						{this.renderOptionsEditor("trafficType", "Traffic Type")}
					</SpaceBetween>
				</Modal> : null}
			</Container>
		);
	}
}

export default PlayerProfilePartnerMetadataSection;
